export const PARTENAIRE_FALLBACK_IMAGE = "/img/no-image.jpg";

export const PARTENAIRE_PHOTO_TARGETS = {
  photo_url: {
    key: "photo_url",
    label: "Logo principal",
    shortLabel: "Logo",
    description: "Image principale affichée dans le carrousel des partenaires.",
  },
  photo2_url: {
    key: "photo2_url",
    label: "Photo secondaire 2",
    shortLabel: "Photo 2",
    description: "Image utilisée sur la page détail du partenaire.",
  },
  photo3_url: {
    key: "photo3_url",
    label: "Photo secondaire 3",
    shortLabel: "Photo 3",
    description: "Deuxième image complémentaire pour la page détail.",
  },
};

export const PARTENAIRE_QUILL_MODULES = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    ["clean"],
  ],
};

export const PARTENAIRE_QUILL_FORMATS = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

export function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

export function toNumber(value, defaultValue = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : defaultValue;
}

export function normalizeText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function sortPartenairesByOrder(rows) {
  return safeArray(rows)
    .slice()
    .sort((a, b) => {
      const orderA = toNumber(a?.ordre, 0);
      const orderB = toNumber(b?.ordre, 0);

      if (orderA !== orderB) return orderA - orderB;

      return String(a?.nom || "").localeCompare(String(b?.nom || ""), "fr");
    });
}

export function normalizeOrderPartenaires(rows) {
  return sortPartenairesByOrder(rows).map((partenaire, index) => ({
    ...partenaire,
    ordre: index + 1,
  }));
}

export function filterPartenaires(partenaires, filters) {
  const query = normalizeText(filters?.query || "");
  const visibility = filters?.visibility || "all";
  const detail = filters?.detail || "all";

  return sortPartenairesByOrder(partenaires).filter((partenaire) => {
    if (visibility === "active" && !partenaire?.is_active) return false;
    if (visibility === "inactive" && partenaire?.is_active) return false;

    if (detail === "detail" && !partenaire?.is_detail) return false;
    if (detail === "no_detail" && partenaire?.is_detail) return false;

    if (!query) return true;

    const searchable = normalizeText(
      `${partenaire?.nom || ""} ${partenaire?.link_url || ""} ${
        partenaire?.tel || ""
      } ${partenaire?.Mail || ""} ${partenaire?.adresse || ""}`
    );

    return searchable.includes(query);
  });
}

export function calculatePartenairesStats(partenaires) {
  const rows = safeArray(partenaires);

  let active = 0;
  let inactive = 0;
  let withDetail = 0;
  let withLogo = 0;

  for (const partenaire of rows) {
    if (partenaire?.is_active) active += 1;
    else inactive += 1;

    if (partenaire?.is_detail) withDetail += 1;
    if (String(partenaire?.photo_url || "").trim()) withLogo += 1;
  }

  return {
    total: rows.length,
    active,
    inactive,
    withDetail,
    withoutDetail: rows.length - withDetail,
    withLogo,
    withoutLogo: rows.length - withLogo,
  };
}

export function getNextPartenaireOrder(partenaires) {
  return safeArray(partenaires).reduce((max, partenaire) => {
    return Math.max(max, toNumber(partenaire?.ordre, 0));
  }, 0) + 1;
}

export function getDefaultPartenaireForm(order = 1) {
  return {
    id: "",
    nom: "",
    photo_url: "",
    link_url: "",
    is_detail: false,
    tel: "",
    adresse: "",
    Mail: "",
    photo2_url: "",
    description2: "",
    photo3_url: "",
    description3: "",
    is_active: true,
    ordre: order,
  };
}

export function mapPartenaireToForm(partenaire, order = 1) {
  if (!partenaire) return getDefaultPartenaireForm(order);

  return {
    id: partenaire?.id || "",
    nom: partenaire?.nom || "",
    photo_url: partenaire?.photo_url || "",
    link_url: partenaire?.link_url || "",
    is_detail: !!partenaire?.is_detail,
    tel: partenaire?.tel || "",
    adresse: partenaire?.adresse || "",
    Mail: partenaire?.Mail || "",
    photo2_url: partenaire?.photo2_url || "",
    description2: partenaire?.description2 || "",
    photo3_url: partenaire?.photo3_url || "",
    description3: partenaire?.description3 || "",
    is_active: partenaire?.is_active ?? true,
    ordre: partenaire?.ordre ?? order,
  };
}

export function buildPartenairePayload(formData) {
  return {
    nom: String(formData?.nom || "").trim(),
    photo_url: String(formData?.photo_url || "").trim(),
    link_url: String(formData?.link_url || "").trim(),
    is_detail: !!formData?.is_detail,
    tel: String(formData?.tel || "").trim(),
    adresse: String(formData?.adresse || "").trim(),
    Mail: String(formData?.Mail || "").trim(),
    photo2_url: String(formData?.photo2_url || "").trim(),
    description2: String(formData?.description2 || ""),
    photo3_url: String(formData?.photo3_url || "").trim(),
    description3: String(formData?.description3 || ""),
    is_active: !!formData?.is_active,
    ordre: toNumber(formData?.ordre, 0),
  };
}

export function validatePartenairePayload(payload) {
  if (!payload.nom) return "Le nom du partenaire est requis.";

  if (!Number.isFinite(Number(payload.ordre))) {
    return "L’ordre d’affichage doit être un nombre.";
  }

  return "";
}

export function validateImageFile(file) {
  if (!file) return "Veuillez sélectionner une image.";

  const validType = /^image\/(png|jpe?g|webp)$/i.test(file.type);

  if (!validType) {
    return "Format non supporté. Utilisez PNG, JPG ou WEBP.";
  }

  if (file.size > 10 * 1024 * 1024) {
    return "Image trop volumineuse. Taille maximale : 10 Mo.";
  }

  return "";
}